import { useCallback } from 'react';

/**
 * Custom hook for smooth scrolling to page sections
 * @param {React.RefObject} headerRef - Reference to the header element for offset calculation
 * @returns {Function} smoothScrollToSection function to use for scrolling
 */
const useSmoothScroll = (headerRef) => {
    /**
     * Smooth scroll to a section by ID with header offset
     * @param {string} sectionId - The ID of the section to scroll to
     * @param {Object} options - Additional options
     * @param {number} options.duration - Animation duration in ms (default: 1000)
     * @param {number} options.additionalOffset - Additional offset in pixels (default: 0)
     * @returns {boolean} Success status of scroll attempt
     */
    const smoothScrollToSection = useCallback((sectionId, options = {}) => {
        const { duration = 1000, additionalOffset = 0 } = options;
        const sectionElement = document.getElementById(sectionId);

        if (!sectionElement) return false;

        // Get element position accounting for any header offset
        const headerHeight = headerRef?.current?.getBoundingClientRect().height || 0;
        const targetPosition = sectionElement.getBoundingClientRect().top + window.pageYOffset - headerHeight - additionalOffset;
        const startPosition = window.pageYOffset;
        const totalDistance = targetPosition - startPosition;

        // Cancel any running animations to prevent conflicts
        if (window.scrollAnimation) {
            window.cancelAnimationFrame(window.scrollAnimation);
        }

        let startTime = null;

        const animation = (currentTime) => {
            if (startTime === null) startTime = currentTime;
            const timeElapsed = currentTime - startTime;

            // Calculate progress (0 to 1)
            const progress = Math.min(timeElapsed / duration, 1);

            // Apply easing (ease-in-out)
            const easeInOut = progress < 0.5
                ? 2 * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 2) / 2;

            // Calculate new position
            const newPosition = startPosition + totalDistance * easeInOut;

            // Apply the scroll
            window.scrollTo(0, newPosition);

            // Continue animation if not complete
            if (progress < 1) {
                window.scrollAnimation = requestAnimationFrame(animation);
            }
        };

        window.scrollAnimation = requestAnimationFrame(animation);
        return true;
    }, [headerRef]);

    return smoothScrollToSection;
};

export default useSmoothScroll; 